import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  createOhtTarget,
  deleteOhtTarget,
  getOhtClinics,
  getOhtInsights,
  getOhtProfile,
  getOhtTargets,
  updateOhtTarget,
  type CreateOhtTargetInput,
  type OhtActiveAdmin,
  type OhtClinic,
  type OhtInsightsResult,
  type OhtProfile,
  type OhtTarget,
  type OhtTargetStatus,
} from "../../../../api/ohtAdmin";
import { Modal } from "../components/Modal";
import { SortCaret, useSortedRows } from "../components/useSortedRows";
import { PageOutline } from "../components/PageOutline";
import "../Overview/OverviewPage.css";
import "../Clinics/ClinicsPage.css";
import "../components/Modal.css";
import "./ProfilePage.css";

type SortKey =
  | "scope"
  | "name"
  | "startDate"
  | "endDate"
  | "targetPatients"
  | "completed"
  | "gap"
  | "daysToTarget"
  | "status";

type ClinicLine = {
  id: number;
  name: string;
  cityId: number | null;
  city: string;
  fsa: string;
  doctorCount: number;
  acceptingDoctorCount: number;
  remainingCapacity: number;
  pipelineCount: number;
  isPublicEligible: boolean;
  governanceStatus: string;
};

type TargetFormState = {
  targetLevel: "FSA";
  fsa: string;
  targetPatients: string;
  startDate: string;
  endDate: string;
};

/**
 * Pure function: compute target progress given pace/day.
 * Kept exported because this logic is useful and testable outside this page.
 */
export function computeTargetProgress(
  targetPatients: number,
  completedAttachments: number,
  pacePerDay: number
): {
  gap: number;
  percentComplete: number;
  daysToTarget: number | null;
  status: "on-track" | "at-risk" | "complete" | "stalled";
} {
  const safeTarget = Math.max(0, Number(targetPatients) || 0);
  const safeCompleted = Math.max(0, Number(completedAttachments) || 0);
  const safePace = Math.max(0, Number(pacePerDay) || 0);
  const gap = Math.max(0, safeTarget - safeCompleted);
  const percent = safeTarget > 0 ? Math.min(1, safeCompleted / safeTarget) : 0;

  if (safeTarget === 0) {
    return { gap: 0, percentComplete: 0, daysToTarget: 0, status: "complete" };
  }

  if (gap === 0) {
    return { gap: 0, percentComplete: 100, daysToTarget: 0, status: "complete" };
  }

  if (safePace <= 0) {
    return {
      gap,
      percentComplete: Math.round(percent * 100),
      daysToTarget: null,
      status: "stalled",
    };
  }

  const days = Math.ceil(gap / safePace);
  const status: "on-track" | "at-risk" = days > 365 ? "at-risk" : "on-track";

  return {
    gap,
    percentComplete: Math.round(percent * 100),
    daysToTarget: days,
    status,
  };
}

function fmtNumber(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString();
}

function fmtRate(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return `${Number(n).toFixed(2)} / day`;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;

  const diffMs = Date.now() - then;
  if (diffMs < 60_000) return "just now";
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)} min ago`;
  if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)} h ago`;
  return `${Math.floor(diffMs / 86_400_000)} d ago`;
}

function statusBadgeClass(status: OhtTargetStatus): string {
  if (status === "complete") return "oht-target-badge oht-target-badge--good";
  if (status === "on-track") return "oht-target-badge oht-target-badge--ok";
  if (status === "at-risk") return "oht-target-badge oht-target-badge--warn";
  return "oht-target-badge oht-target-badge--danger";
}

function statusLabel(status: OhtTargetStatus): string {
  if (status === "complete") return "Complete";
  if (status === "on-track") return "On track";
  if (status === "at-risk") return "At risk";
  return "Stalled";
}

function targetScopeLabel(target: OhtTarget): string {
  if (target.targetLevel === "OHT") return "Whole OHT";
  if (target.targetLevel === "FSA") {
    return target.fsaLabel || (target.fsa ? `FSA ${target.fsa}` : "FSA target");
  }
  return target.scopeLabel || target.targetLevel;
}

function targetScopeCss(target: OhtTarget): string {
  const scope = String(target.scope || target.targetLevel || "OHT").toLowerCase();
  if (scope === "fsa") return "oht-target-scope-city";
  if (scope === "oht") return "oht-target-scope-oht";
  return `oht-target-scope-${scope}`;
}

function toClinicLine(clinic: OhtClinic): ClinicLine {
  return {
    id: clinic.clinicId,
    name: clinic.clinicName,
    cityId: clinic.cityId,
    city: clinic.cityName || "—",
    fsa: clinic.normalizedFsa || "—",
    doctorCount: clinic.doctorCount || 0,
    acceptingDoctorCount: clinic.acceptingDoctorCount || 0,
    remainingCapacity: clinic.remainingCapacity || 0,
    pipelineCount: clinic.pipelineCount || 0,
    isPublicEligible: !!clinic.isPublicEligible,
    governanceStatus: clinic.governanceStatus || "—",
  };
}

function todayDateString(): string {
  return new Date().toISOString().slice(0, 10);
}

function oneYearFromTodayDateString(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

function normalizeFsa(value: unknown): string {
  return String(value || "").trim().replace(/\s+/g, "").toUpperCase();
}

function isFsaTarget(target: OhtTarget): boolean {
  return target.targetLevel === "FSA" || target.scope === "FSA";
}

function isLegacyWholeOhtTarget(target: OhtTarget): boolean {
  return target.targetLevel === "OHT" || target.scope === "OHT";
}

function sumNumbers(values: Array<number | null | undefined>): number {
  return values.reduce<number>((total, value) => {
    return total + (Number(value) || 0);
  }, 0);
}

function emptyTargetForm(): TargetFormState {
  return {
    targetLevel: "FSA",
    fsa: "",
    targetPatients: "",
    startDate: todayDateString(),
    endDate: oneYearFromTodayDateString(),
  };
}

function formFromTarget(target: OhtTarget): TargetFormState {
  return {
    targetLevel: "FSA",
    fsa: normalizeFsa(target.fsa),
    targetPatients:
      target.targetPatients === null || target.targetPatients === undefined
        ? ""
        : String(target.targetPatients),
    startDate: target.startDate || todayDateString(),
    endDate: target.endDate || oneYearFromTodayDateString(),
  };
}

function isTargetCurrentlyActive(target: OhtTarget): boolean {
  const today = new Date();
  const start = target.startDate ? new Date(target.startDate) : null;
  const end = target.endDate ? new Date(target.endDate) : null;

  if (!start || Number.isNaN(start.getTime())) return false;
  if (start > today) return false;

  if (!end || Number.isNaN(end.getTime())) return true;
  return end >= today;
}

export default function ProfilePage() {
  const [profile, setProfile] = useState<OhtProfile | null>(null);
  const [clinics, setClinics] = useState<ClinicLine[]>([]);
  const [targets, setTargets] = useState<OhtTarget[]>([]);
  const [insights, setInsights] = useState<OhtInsightsResult | null>(null);

  const [loading, setLoading] = useState(true);
  const [savingTarget, setSavingTarget] = useState(false);
  const [deletingTargetId, setDeletingTargetId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [fsaFilter, setFsaFilter] = useState<string | null>(null);

  const [targetModalOpen, setTargetModalOpen] = useState(false);
  const [editingTarget, setEditingTarget] = useState<OhtTarget | null>(null);
  const [targetForm, setTargetForm] = useState<TargetFormState>(() => emptyTargetForm());

  const [projectionTarget, setProjectionTarget] = useState<number>(0);
  const [projectionCompleted, setProjectionCompleted] = useState<number>(0);
  const [projectionPace, setProjectionPace] = useState<number>(0);
  const [projectionInitialized, setProjectionInitialized] = useState(false);

  const loadLiveData = useCallback(async (signal?: AbortSignal) => {
    const [profileResp, clinicsResp, targetsResp, insightsResp] = await Promise.all([
      getOhtProfile(signal),
      getOhtClinics(signal),
      getOhtTargets(signal),
      getOhtInsights({}, signal),
    ]);

    setProfile(profileResp);
    setClinics(
      (clinicsResp.items || [])
        .map(toClinicLine)
        .sort((a, b) => a.name.localeCompare(b.name))
    );
    setTargets(targetsResp.items || []);
    setInsights(insightsResp);

    return { targetsResp, insightsResp };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const { targetsResp, insightsResp } = await loadLiveData(controller.signal);
        if (cancelled) return;

        if (!projectionInitialized) {
          const allFsaTargets = (targetsResp.items || []).filter(isFsaTarget);
          const activeFsaTargets = allFsaTargets.filter(isTargetCurrentlyActive);
          const fsaTargetsForProjection = activeFsaTargets.length > 0 ? activeFsaTargets : allFsaTargets;

          if (fsaTargetsForProjection.length > 0) {
            setProjectionTarget(
              sumNumbers(fsaTargetsForProjection.map((target) => target.targetPatients))
            );
            setProjectionCompleted(
              sumNumbers(fsaTargetsForProjection.map((target) => target.completed))
            );
            setProjectionPace(
              sumNumbers(fsaTargetsForProjection.map((target) => target.pacePerDay)) ||
                insightsResp.throughput.pacePerDay ||
                0
            );
          } else {
            setProjectionTarget(0);
            setProjectionCompleted(0);
            setProjectionPace(insightsResp.throughput.pacePerDay || 0);
          }

          setProjectionInitialized(true);
        }
      } catch (err: any) {
        if (!cancelled && err?.name !== "AbortError") {
          setError(String(err?.message || "Failed to load OHT profile."));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [loadLiveData, projectionInitialized]);

  const projection = useMemo(
    () => computeTargetProgress(projectionTarget, projectionCompleted, projectionPace),
    [projectionTarget, projectionCompleted, projectionPace]
  );

  const getSortValue = useCallback(
    (target: OhtTarget, key: SortKey): string | number | null => {
      switch (key) {
        case "scope":
          return `${target.scope} ${targetScopeLabel(target)}`;
        case "name":
          return target.name;
        case "startDate":
          return target.startDate;
        case "endDate":
          return target.endDate;
        case "targetPatients":
          return target.targetPatients;
        case "completed":
          return target.completed;
        case "gap":
          return target.gap;
        case "daysToTarget":
          return target.estimatedDaysToTarget;
        case "status":
          return target.status;
      }
    },
    []
  );

  const fsaTargets = useMemo(
    () => targets.filter(isFsaTarget),
    [targets]
  );

  const legacyWholeOhtTargets = useMemo(
    () => targets.filter(isLegacyWholeOhtTarget),
    [targets]
  );

  const { sorted, sort, toggleSort } = useSortedRows<OhtTarget, SortKey>(
    fsaTargets,
    getSortValue,
    { key: "status", direction: "asc" }
  );

  const visibleClinics = useMemo(() => {
    if (!fsaFilter) return clinics;
    return clinics.filter((clinic) => clinic.fsa === fsaFilter);
  }, [clinics, fsaFilter]);

  const fsaCounts = useMemo(() => {
    const map = new Map<string, number>();
    clinics.forEach((clinic) => {
      map.set(clinic.fsa, (map.get(clinic.fsa) || 0) + 1);
    });
    return map;
  }, [clinics]);

  const fsaOptions = useMemo(() => {
    const set = new Set<string>();

    (profile?.fsas || []).forEach((fsa) => {
      const normalized = normalizeFsa(fsa);
      if (normalized) set.add(normalized);
    });

    clinics.forEach((clinic) => {
      const normalized = normalizeFsa(clinic.fsa);
      if (normalized && normalized !== "—") set.add(normalized);
    });

    return Array.from(set).sort();
  }, [profile?.fsas, clinics]);

  const activeFsaTargets = useMemo(
    () => fsaTargets.filter(isTargetCurrentlyActive),
    [fsaTargets]
  );

  const fsaTargetsForCalculatedTotal = activeFsaTargets.length > 0 ? activeFsaTargets : fsaTargets;

  const calculatedOhtTarget = useMemo(() => {
    const targetPatients = sumNumbers(
      fsaTargetsForCalculatedTotal.map((target) => target.targetPatients)
    );
    const completed = sumNumbers(
      fsaTargetsForCalculatedTotal.map((target) => target.completed)
    );
    const pacePerDay = sumNumbers(
      fsaTargetsForCalculatedTotal.map((target) => target.pacePerDay)
    );
    const progress = computeTargetProgress(targetPatients, completed, pacePerDay);

    return {
      targetPatients,
      completed,
      pacePerDay,
      ...progress,
    };
  }, [fsaTargetsForCalculatedTotal]);

  const calculatedOhtTargetBasisLabel =
    activeFsaTargets.length > 0
      ? `${activeFsaTargets.length} active FSA target${activeFsaTargets.length === 1 ? "" : "s"}`
      : fsaTargets.length > 0
      ? `${fsaTargets.length} saved FSA target${fsaTargets.length === 1 ? "" : "s"}`
      : "No FSA targets yet";

  function openCreateTarget() {
    setEditingTarget(null);
    setTargetForm(emptyTargetForm());
    setSuccess(null);
    setError(null);
    setTargetModalOpen(true);
  }

  function openEditTarget(target: OhtTarget) {
    if (!isFsaTarget(target)) {
      setError("Only FSA targets can be edited. The whole-OHT target is calculated from the total of all FSA targets.");
      return;
    }

    setEditingTarget(target);
    setTargetForm(formFromTarget(target));
    setSuccess(null);
    setError(null);
    setTargetModalOpen(true);
  }

  function closeTargetModal() {
    setTargetModalOpen(false);
    setEditingTarget(null);
    setTargetForm(emptyTargetForm());
  }

  function updateTargetForm<K extends keyof TargetFormState>(
    key: K,
    value: TargetFormState[K]
  ) {
    setTargetForm((prev) => {
      const next = { ...prev, [key]: value };

      if (key === "targetLevel") {
        next.fsa = "";
      }

      if (key === "fsa") {
        next.fsa = normalizeFsa(value);
      }

      return next;
    });
  }

  async function handleSaveTarget(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const trimmedTargetPatients = targetForm.targetPatients.trim();
    const targetPatients = Number(trimmedTargetPatients);

    if (trimmedTargetPatients === "") {
      setError("Target patients is required. Use 0 only when intentionally setting no target for an FSA.");
      return;
    }

    if (!Number.isInteger(targetPatients) || targetPatients < 0) {
      setError("Target patients must be a whole number of 0 or greater.");
      return;
    }

    if (!targetForm.startDate || !targetForm.endDate) {
      setError("Start date and end date are required.");
      return;
    }

    if (new Date(targetForm.endDate).getTime() < new Date(targetForm.startDate).getTime()) {
      setError("End date must be on or after start date.");
      return;
    }

    const fsa = normalizeFsa(targetForm.fsa);

    if (!fsa) {
      setError("Please select an FSA for this target.");
      return;
    }

    if (!/^[A-Z0-9]{3}$/.test(fsa)) {
      setError("FSA must be a valid 3-character postal prefix.");
      return;
    }

    const payload: CreateOhtTargetInput = {
      targetLevel: "FSA",
      fsa,
      targetPatients,
      startDate: targetForm.startDate,
      endDate: targetForm.endDate,
    };

    setSavingTarget(true);

    try {
      if (editingTarget) {
        const updated = await updateOhtTarget(editingTarget.ohtTargetId, payload);
        setTargets((prev) =>
          prev.map((row) => (row.ohtTargetId === updated.ohtTargetId ? updated : row))
        );
        setSuccess("Target updated.");
      } else {
        const created = await createOhtTarget(payload);
        setTargets((prev) => [created, ...prev]);
        setSuccess("Target created.");
      }

      const [freshTargets, freshInsights] = await Promise.all([
        getOhtTargets(),
        getOhtInsights({}),
      ]);

      setTargets(freshTargets.items || []);
      setInsights(freshInsights);
      closeTargetModal();
    } catch (err: any) {
      setError(String(err?.message || "Failed to save target."));
    } finally {
      setSavingTarget(false);
    }
  }

  async function handleDeleteTarget(target: OhtTarget) {
    setError(null);
    setSuccess(null);

    if (!isFsaTarget(target) && !isLegacyWholeOhtTarget(target)) {
      setError("Only FSA targets and legacy whole-OHT targets can be deleted from this page.");
      return;
    }

    const label = targetScopeLabel(target);
    const confirmed = window.confirm(
      `Delete ${label}? This removes the saved target and recalculates the whole-OHT total from the remaining FSA targets.`
    );

    if (!confirmed) return;

    setDeletingTargetId(target.ohtTargetId);

    try {
      await deleteOhtTarget(target.ohtTargetId);

      const [freshTargets, freshInsights] = await Promise.all([
        getOhtTargets(),
        getOhtInsights({}),
      ]);

      setTargets(freshTargets.items || []);
      setInsights(freshInsights);
      setSuccess("Target deleted.");
    } catch (err: any) {
      setError(String(err?.message || "Failed to delete target."));
    } finally {
      setDeletingTargetId(null);
    }
  }

  return (
    <div className="oht-page">
      <header className="oht-page-header">
        <div className="oht-page-title-row">
          <div>
            <h1 className="oht-page-title">Profile</h1>
            <p className="oht-page-subtitle">
              OHT identity, FSA coverage, scoped clinics, FSA attachment targets, calculated OHT target total, and OHT Admin roster.
            </p>
          </div>
        </div>
      </header>

      {error ? <div className="oht-page-error">{error}</div> : null}
      {success ? <div className="oht-page-success">{success}</div> : null}

      <section className="ocf-card oht-profile-block">
        <h2 className="ocf-section-title">OHT details</h2>
        <p className="oht-profile-note">
          Read-only for OHT Admin users. OHT identity and FSA coverage should be edited only by Super Admin because FSA changes affect clinic scoping and reporting.
        </p>
        <dl className="oht-profile-kv">
          <dt>OHT name</dt>
          <dd>{loading ? "Loading…" : profile?.name || "—"}</dd>
          <dt>Code</dt>
          <dd className="oht-table-mono">{loading ? "Loading…" : profile?.code || "—"}</dd>
          <dt>Active</dt>
          <dd>{profile ? (profile.isActive ? "Yes" : "No") : "—"}</dd>
          <dt>Created</dt>
          <dd>{fmtDateTime(profile?.createdAt)}</dd>
          <dt>Updated</dt>
          <dd>{fmtDateTime(profile?.updatedAt)}</dd>
          <dt>Your access</dt>
          <dd>{profile?.currentUser?.email || "—"}</dd>
        </dl>
      </section>

      <section className="ocf-card oht-profile-block">
        <div className="oht-section-head">
          <h2 className="ocf-section-title">Live OHT scope</h2>
          <span className="oht-section-hint">FSA-based clinic membership</span>
        </div>
        <div className="oht-kpi-grid">
          <KpiTile label="FSAs covered" value={fmtNumber(profile?.fsas.length)} hint="Postal-code prefixes assigned to this OHT." />
          <KpiTile label="Clinics" value={fmtNumber(profile?.clinicCount)} hint="Clinics matched by normalized FSA." />
          <KpiTile label="Family doctors" value={fmtNumber(profile?.doctorCount)} hint={`${fmtNumber(profile?.acceptingDoctorCount)} accepting new patient requests.`} />
          <KpiTile label="Active OHT admins" value={fmtNumber(profile?.activeAdminCount)} hint="Users with active OHTAdmin access." />
        </div>
      </section>

      <section className="ocf-card oht-profile-block">
        <div className="oht-section-head">
          <h2 className="ocf-section-title">FSAs covered</h2>
          <span className="oht-section-hint">
            {profile?.fsas.length ?? 0} postal-code prefixes · click to filter clinics
          </span>
        </div>
        <p className="oht-profile-note">
          A clinic belongs to this OHT when <code>Clinic.NormalizedFSA</code> matches one of these OHT coverage FSAs.
        </p>
        <div className="oht-fsa-chips">
          {(profile?.fsas || []).map((fsa) => {
            const count = fsaCounts.get(fsa) || 0;
            const active = fsaFilter === fsa;
            return (
              <button
                key={fsa}
                type="button"
                className={`oht-fsa-chip oht-fsa-chip--btn${active ? " oht-fsa-chip--active" : ""}`}
                onClick={() => setFsaFilter(active ? null : fsa)}
                aria-pressed={active}
                title={`${count} clinic${count === 1 ? "" : "s"} in ${fsa}`}
              >
                <span className="oht-fsa-chip-code">{fsa}</span>
                <span className="oht-fsa-chip-count">{count}</span>
              </button>
            );
          })}
          {!loading && profile && profile.fsas.length === 0 ? (
            <span className="oht-profile-note">No FSAs are currently assigned to this OHT.</span>
          ) : null}
        </div>
      </section>

      <section className="ocf-card oht-profile-block">
        <div className="oht-section-head">
          <h2 className="ocf-section-title">Clinics in OHT scope</h2>
          <span className="oht-section-hint">
            {fsaFilter
              ? `${visibleClinics.length} in ${fsaFilter} of ${clinics.length} total`
              : `${clinics.length} clinic${clinics.length === 1 ? "" : "s"}`}
          </span>
        </div>
        {fsaFilter ? (
          <div className="oht-fsa-filter-bar">
            <span className="oht-fsa-filter-bar-label">
              Filtered by FSA <span className="oht-fsa-filter-bar-code">{fsaFilter}</span>
            </span>
            <button
              type="button"
              className="oht-fsa-filter-bar-clear"
              onClick={() => setFsaFilter(null)}
            >
              Clear filter
            </button>
          </div>
        ) : null}
        <ul className="oht-profile-clinic-list">
          {loading ? (
            <li className="oht-profile-empty">Loading clinics…</li>
          ) : visibleClinics.length > 0 ? (
            visibleClinics.map((clinic) => (
              <li key={clinic.id} className="oht-profile-clinic">
                <div>
                  <div className="oht-profile-clinic-name">{clinic.name}</div>
                  <div className="oht-profile-clinic-sub">
                    {clinic.city} · <span className="oht-table-mono">{clinic.fsa}</span>
                    {" · "}
                    {clinic.doctorCount} doctor{clinic.doctorCount === 1 ? "" : "s"}
                    {" · "}
                    {clinic.acceptingDoctorCount} accepting
                    {" · "}
                    {fmtNumber(clinic.remainingCapacity)} remaining capacity
                    {" · "}
                    {fmtNumber(clinic.pipelineCount)} active pipeline
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <span className={`oht-profile-pill ${clinic.isPublicEligible ? "oht-profile-pill--ok" : "oht-profile-pill--owner"}`}>
                    {clinic.isPublicEligible ? "Public eligible" : "Incomplete"}
                  </span>
                  <span className="oht-profile-pill oht-profile-pill--ok">
                    {clinic.governanceStatus}
                  </span>
                </div>
              </li>
            ))
          ) : (
            <li className="oht-profile-empty">
              {fsaFilter ? `No clinics in ${fsaFilter}.` : "No clinics returned for this OHT."}
            </li>
          )}
        </ul>
      </section>

      <section className="ocf-card oht-table-card">
        <div className="oht-section-head">
          <div>
            <h2 className="ocf-section-title">Attachment targets ({loading ? "…" : sorted.length})</h2>
            <p className="oht-profile-note" style={{ margin: 0 }}>
              Only FSA targets can be created or edited. The whole-OHT target is read-only and calculated from the total of all FSA targets. City, clinic, and doctor views are derived from live data.
            </p>
          </div>
          <div className="oht-section-head-right">
            <button
              type="button"
              className="ocf-btn ocf-btn--primary"
              onClick={openCreateTarget}
            >
              + New target
            </button>
          </div>
        </div>

        <div className="oht-kpi-grid" style={{ marginBottom: 16 }}>
          <KpiTile
            label="Calculated whole-OHT target"
            value={loading ? "Loading…" : fmtNumber(calculatedOhtTarget.targetPatients)}
            hint={`Sum of ${calculatedOhtTargetBasisLabel}.`}
          />
          <KpiTile
            label="Completed against FSA targets"
            value={loading ? "Loading…" : fmtNumber(calculatedOhtTarget.completed)}
            hint="Summed from the same FSA target rows."
          />
          <KpiTile
            label="Calculated OHT gap"
            value={loading ? "Loading…" : fmtNumber(calculatedOhtTarget.gap)}
            hint="Calculated whole-OHT target minus completed attachments."
          />
          <KpiTile
            label="Estimated time to target"
            value={
              loading
                ? "Loading…"
                : calculatedOhtTarget.daysToTarget == null
                ? "—"
                : calculatedOhtTarget.daysToTarget === 0
                ? "Complete"
                : `${fmtNumber(calculatedOhtTarget.daysToTarget)} d`
            }
            hint={`Calculated pace: ${fmtRate(calculatedOhtTarget.pacePerDay)}.`}
          />
        </div>

        {legacyWholeOhtTargets.length > 0 ? (
          <div className="oht-page-error" style={{ marginTop: 0 }}>
            <div>
              {legacyWholeOhtTargets.length} legacy whole-OHT target
              {legacyWholeOhtTargets.length === 1 ? "" : "s"} found. Whole-OHT targets are no longer editable or used in this page. Delete them so the OHT total comes only from FSA targets.
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
              {legacyWholeOhtTargets.map((target) => (
                <button
                  key={target.ohtTargetId}
                  type="button"
                  className="ocf-btn ocf-btn--secondary oht-table-action"
                  onClick={() => handleDeleteTarget(target)}
                  disabled={deletingTargetId === target.ohtTargetId}
                >
                  {deletingTargetId === target.ohtTargetId
                    ? "Deleting…"
                    : `Delete ${targetScopeLabel(target)} (${fmtNumber(target.targetPatients)})`}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <div className="oht-table-wrap">
          <table className="oht-table oht-table--targets">
            <colgroup>
              <col style={{ width: "18%" }} />
              <col style={{ width: "19%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "10%" }} />
            </colgroup>
            <thead>
              <tr>
                <Th sortKey="scope" label="Scope" sort={sort} onSort={toggleSort} />
                <Th sortKey="name" label="Name" sort={sort} onSort={toggleSort} />
                <Th sortKey="startDate" label="Start" sort={sort} onSort={toggleSort} />
                <Th sortKey="endDate" label="End" sort={sort} onSort={toggleSort} />
                <Th sortKey="targetPatients" label="Target" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="completed" label="Completed" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="gap" label="Gap" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="daysToTarget" label="ETA" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="status" label="Status" sort={sort} onSort={toggleSort} />
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="oht-table-empty">Loading targets…</td>
                </tr>
              ) : sorted.length === 0 ? (
                <tr>
                  <td colSpan={10} className="oht-table-empty">No saved FSA targets yet.</td>
                </tr>
              ) : (
                sorted.map((target) => (
                  <tr key={target.ohtTargetId}>
                    <td>
                      <div className="oht-target-scope">
                        <span className={`oht-target-scope-tag ${targetScopeCss(target)}`}>
                          {target.targetLevel}
                        </span>
                        <span className="oht-target-scope-label">{targetScopeLabel(target)}</span>
                      </div>
                    </td>
                    <td className="oht-doc-name">{target.name}</td>
                    <td>{fmtDate(target.startDate)}</td>
                    <td>{fmtDate(target.endDate)}</td>
                    <td className="oht-table-num">{fmtNumber(target.targetPatients)}</td>
                    <td className="oht-table-num">
                      <div className="oht-progress-cell">
                        <span>{fmtNumber(target.completed)}</span>
                        <span className="oht-progress-pct">{target.percentComplete == null ? "—" : `${target.percentComplete}%`}</span>
                      </div>
                    </td>
                    <td className="oht-table-num">{fmtNumber(target.gap)}</td>
                    <td className="oht-table-num">
                      {target.estimatedDaysToTarget == null
                        ? "—"
                        : target.estimatedDaysToTarget === 0
                        ? "Complete"
                        : `${fmtNumber(target.estimatedDaysToTarget)} d`}
                    </td>
                    <td>
                      <span className={statusBadgeClass(target.status)}>
                        {statusLabel(target.status)}
                      </span>
                    </td>
                    <td className="oht-table-num">
                      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
                        <button
                          type="button"
                          className="ocf-btn ocf-btn--secondary oht-table-action"
                          onClick={() => openEditTarget(target)}
                          disabled={deletingTargetId === target.ohtTargetId}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="ocf-btn ocf-btn--secondary oht-table-action"
                          onClick={() => handleDeleteTarget(target)}
                          disabled={deletingTargetId === target.ohtTargetId}
                        >
                          {deletingTargetId === target.ohtTargetId ? "Deleting…" : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="ocf-card oht-projection">
        <div className="oht-section-head">
          <h2 className="ocf-section-title">Attachment target projection</h2>
          <span className="oht-section-hint">
            {fsaTargets.length > 0 ? "Seeded from calculated FSA total" : "What-if calculator"}
          </span>
        </div>
        <p className="oht-profile-note" style={{ marginTop: 0 }}>
          Model a hypothetical target using live attachment pace or any custom pace. This calculator does not save changes.
        </p>

        <div className="oht-projection-tool">
          <div className="oht-projection-inputs">
            <div className="oht-projection-panel-head">
              <h3 className="oht-projection-subhead">Your inputs</h3>
              <span className="oht-projection-panel-hint">Edit any value</span>
            </div>
            <div className="oht-projection-input-grid">
              <div className="oht-filter">
                <label className="oht-filter-label" htmlFor="proj-target">Target patients</label>
                <input
                  id="proj-target"
                  className="ocf-select"
                  type="number"
                  min={0}
                  value={projectionTarget}
                  onChange={(e) => setProjectionTarget(Math.max(0, Number(e.target.value) || 0))}
                />
              </div>
              <div className="oht-filter">
                <label className="oht-filter-label" htmlFor="proj-completed">Completed attachments</label>
                <input
                  id="proj-completed"
                  className="ocf-select"
                  type="number"
                  min={0}
                  value={projectionCompleted}
                  onChange={(e) => setProjectionCompleted(Math.max(0, Number(e.target.value) || 0))}
                />
              </div>
              <div className="oht-filter">
                <label className="oht-filter-label" htmlFor="proj-pace">Attachment pace</label>
                <input
                  id="proj-pace"
                  className="ocf-select"
                  type="number"
                  min={0}
                  step="0.1"
                  value={projectionPace}
                  onChange={(e) => setProjectionPace(Math.max(0, Number(e.target.value) || 0))}
                />
                <div className="oht-muted">Live current pace: {fmtRate(insights?.throughput.pacePerDay)}</div>
              </div>
            </div>
          </div>

          <div className="oht-projection-connector" aria-hidden="true">
            <span className="oht-projection-connector-arrow">↓</span>
            <span className="oht-projection-connector-label">Computed live</span>
          </div>

          <div className="oht-projection-results">
            <div className="oht-projection-panel-head">
              <h3 className="oht-projection-subhead">Projected outcome</h3>
              <ProjectionStatusBadge status={projection.status} />
            </div>

            <div className="oht-projection-metrics">
              <div className="oht-projection-metric">
                <div className="oht-projection-metric-label">Percent complete</div>
                <div className="oht-projection-metric-value">{projection.percentComplete}%</div>
                <div className="oht-projection-progress">
                  <div
                    className="oht-projection-progress-fill"
                    style={{ width: `${Math.min(100, projection.percentComplete)}%` }}
                  />
                </div>
              </div>

              <div className="oht-projection-metric">
                <div className="oht-projection-metric-label">Remaining gap</div>
                <div className="oht-projection-metric-value">{fmtNumber(projection.gap)}</div>
                <div className="oht-projection-metric-sub">
                  {projection.gap === 0 ? "Target reached" : "patients still to attach"}
                </div>
              </div>

              <div className="oht-projection-metric">
                <div className="oht-projection-metric-label">Estimated days to target</div>
                <div className="oht-projection-metric-value">
                  {projection.daysToTarget == null
                    ? "—"
                    : projection.daysToTarget === 0
                    ? "0"
                    : fmtNumber(projection.daysToTarget)}
                </div>
                <div className="oht-projection-metric-sub">
                  {projection.daysToTarget == null
                    ? "pace is zero"
                    : projection.daysToTarget === 0
                    ? "already complete"
                    : `at current pace (${fmtRate(projectionPace)})`}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="ocf-card oht-profile-block">
        <div className="oht-section-head">
          <h2 className="ocf-section-title">OHT admins</h2>
          <span className="oht-section-hint">Read-only roster from live profile endpoint</span>
        </div>
        <p className="oht-profile-note">
          OHT Admin role assignment is controlled outside this page. Future invite/remove workflows can be added once role-management endpoints are available.
        </p>
        <ul className="oht-profile-clinic-list">
          {loading ? (
            <li className="oht-profile-empty">Loading admins…</li>
          ) : profile?.activeAdmins && profile.activeAdmins.length > 0 ? (
            profile.activeAdmins.map((admin) => (
              <AdminRow key={admin.userRoleId} admin={admin} currentUserRoleId={profile.currentUser.userRoleId} />
            ))
          ) : (
            <li className="oht-profile-empty">No OHT admins returned.</li>
          )}
        </ul>
      </section>

      <PageOutline
        whatItShows="This page shows the OHT profile, the postal-code areas covered by the OHT, the clinics included in that scope, FSA attachment targets, the calculated whole-OHT target total, a target projection tool, and the OHT Admin roster."
        audience="For OHT Admin users who need to understand the OHT’s coverage area, monitor attachment targets, and review which clinics and admins are connected to the OHT."
        updateTrigger="The page refreshes when it opens. Adding, editing, or deleting an FSA target updates the target list, calculated whole-OHT total, and projection information."
        dataSources={[
          {
            label: "OHT profile",
            status: "live",
            detail: "Shows the OHT name, code, active status, covered FSAs, and active OHT Admin users.",
          },
          {
            label: "Clinics in scope",
            status: "live",
            detail: "Shows clinics that fall within the OHT’s covered postal-code areas, including clinic counts, accepting doctors, remaining capacity, and active pipeline.",
          },
          {
            label: "Attachment targets",
            status: "live",
            detail: "Shows saved FSA attachment targets. The whole-OHT target is calculated from the total of the FSA targets.",
          },
          {
            label: "Target projection",
            status: "derived",
            detail: "Helps estimate the remaining gap and time to target using completed attachments and current attachment pace.",
          },
        ]}
        notes={[
          "Use this page to confirm that the OHT’s coverage area is correct and that clinics are appearing under the expected FSAs.",
          "FSA targets can be set to any whole number, including 0, or deleted if they are no longer needed.",
          "The whole-OHT target is not entered manually. It is calculated from the FSA targets so the total stays consistent.",
          "The projection tool is for planning support only and should not be treated as a guarantee of future attachment timelines.",
          "OHT identity, FSA coverage, and admin role changes are governance items and should be handled carefully.",
        ]}
      />

      <Modal
        open={targetModalOpen}
        title={editingTarget ? "Edit target" : "New target"}
        eyebrow="Attachment target"
        onClose={closeTargetModal}
        size="md"
      >
        <form onSubmit={handleSaveTarget} className="oht-target-form">
          <div className="oht-form-grid">
            <div className="oht-filter">
              <label className="oht-filter-label" htmlFor="target-fsa">FSA</label>
              <select
                id="target-fsa"
                className="ocf-select"
                value={targetForm.fsa}
                onChange={(e) => updateTargetForm("fsa", e.target.value)}
              >
                <option value="">Select FSA</option>
                {fsaOptions.map((fsa) => (
                  <option key={fsa} value={fsa}>
                    {fsa} · {fmtNumber(fsaCounts.get(fsa) || 0)} clinic
                    {(fsaCounts.get(fsa) || 0) === 1 ? "" : "s"}
                  </option>
                ))}
              </select>
              <div className="oht-muted">
                Saved targets are FSA-only. The whole-OHT target is calculated automatically from all FSA targets.
              </div>
            </div>

            <div className="oht-filter">
              <label className="oht-filter-label" htmlFor="target-patients">Target patients</label>
              <input
                id="target-patients"
                className="ocf-select"
                type="number"
                min={0}
                value={targetForm.targetPatients}
                onChange={(e) => updateTargetForm("targetPatients", e.target.value)}
              />
            </div>

            <div className="oht-filter">
              <label className="oht-filter-label" htmlFor="target-start">Start date</label>
              <input
                id="target-start"
                className="ocf-select"
                type="date"
                value={targetForm.startDate}
                onChange={(e) => updateTargetForm("startDate", e.target.value)}
              />
            </div>

            <div className="oht-filter">
              <label className="oht-filter-label" htmlFor="target-end">End date</label>
              <input
                id="target-end"
                className="ocf-select"
                type="date"
                value={targetForm.endDate}
                onChange={(e) => updateTargetForm("endDate", e.target.value)}
              />
            </div>
          </div>

          <div className="oht-form-actions">
            <button type="submit" className="ocf-btn ocf-btn--primary" disabled={savingTarget}>
              {savingTarget ? "Saving…" : editingTarget ? "Save target" : "Create target"}
            </button>
            <button type="button" className="ocf-btn ocf-btn--secondary" onClick={closeTargetModal} disabled={savingTarget}>
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function AdminRow({ admin, currentUserRoleId }: { admin: OhtActiveAdmin; currentUserRoleId: number }) {
  const isCurrentUser = admin.userRoleId === currentUserRoleId;
  return (
    <li className="oht-profile-clinic">
      <div>
        <div className="oht-profile-clinic-name">
          {admin.displayName || admin.email}
          {isCurrentUser ? " (you)" : ""}
        </div>
        <div className="oht-profile-clinic-sub">
          {admin.email} · activated {fmtRelative(admin.activatedAt)}
        </div>
      </div>
      <span className={`oht-profile-pill ${isCurrentUser ? "oht-profile-pill--owner" : "oht-profile-pill--ok"}`}>
        OHT Admin
      </span>
    </li>
  );
}

function KpiTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint: string;
}) {
  return (
    <div className="ocf-card ocf-kpi">
      <div className="ocf-kpi-label">{label}</div>
      <div className="ocf-big-number">{value}</div>
      <div className="ocf-muted">{hint}</div>
    </div>
  );
}

function ProjectionStatusBadge({
  status,
}: {
  status: "on-track" | "at-risk" | "complete" | "stalled";
}) {
  const map: Record<typeof status, { label: string; tone: string; icon: string }> = {
    complete: { label: "Complete", tone: "good", icon: "✓" },
    "on-track": { label: "On track", tone: "ok", icon: "●" },
    "at-risk": { label: "At risk", tone: "warn", icon: "!" },
    stalled: { label: "Stalled", tone: "danger", icon: "✕" },
  };
  const { label, tone, icon } = map[status];
  return (
    <span className={`oht-projection-status-badge oht-projection-status-badge--${tone}`}>
      <span className="oht-projection-status-icon">{icon}</span>
      {label}
    </span>
  );
}

function Th({
  sortKey,
  label,
  sort,
  onSort,
  numeric,
}: {
  sortKey: SortKey;
  label: string;
  sort: { key: SortKey | null; direction: "asc" | "desc" };
  onSort: (key: SortKey) => void;
  numeric?: boolean;
}) {
  return (
    <th
      className={`oht-th-sortable ${numeric ? "oht-table-num" : ""}`}
      onClick={() => onSort(sortKey)}
      aria-sort={
        sort.key === sortKey
          ? sort.direction === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      <span className="oht-th-label">{label}</span>
      <SortCaret
        activeKey={sort.key}
        columnKey={sortKey}
        direction={sort.direction}
      />
    </th>
  );
}